import { useCallback, useRef, useState, type DragEvent } from "react";
import { TFunction } from "i18next";
import { CDTAndCustomProcedureValue } from "../../../components/CDTAndCustomProcedureSelect";
import {
  treatmentPlanService,
  TreatmentPlanResponse,
  AppointmentUpdateData,
  StepUpdateData,
  TreatmentPlanAppointment,
} from "../../../services/treatmentPlanService";
import { CDTCode } from "../../../services/cdtCodesService";
import {
  EditableAppointmentData,
  EditableProcedureData,
} from "../../../types/editableTreatmentPlan";
import { convertToEditableAppointment } from "../../../utils/treatmentPlanAppointmentUtils";
import {
  cloneEditableAppointment,
  computeAppointmentDuration,
  hasAppointmentDraftChanges,
  restoreAppointmentFromInitialDraft,
  syncProcedureAfterCdtChange,
  syncProcedureIsFeeEdited,
} from "../../../utils/treatmentPlanDraftUtils";
import {
  saveTreatmentPlanUpdate,
} from "../../../utils/treatmentPlanSaveUtils";

type NotificationSeverity = "success" | "error";

interface UseTreatmentPlanAppointmentsParams {
  treatmentPlan: TreatmentPlanResponse | null;
  setTreatmentPlan: React.Dispatch<
    React.SetStateAction<TreatmentPlanResponse | null>
  >;
  planId: string | undefined;
  cdtCodes: CDTCode[];
  canEditAppointments: boolean;
  showNotification: (
    message: string,
    severity?: NotificationSeverity,
  ) => void;
  onRefetchPlan: (force?: boolean) => Promise<unknown>;
  t: TFunction;
}

export function useTreatmentPlanAppointments({
  treatmentPlan,
  setTreatmentPlan,
  planId,
  cdtCodes,
  canEditAppointments,
  showNotification,
  onRefetchPlan,
  t,
}: UseTreatmentPlanAppointmentsParams) {
  const [appointments, setAppointments] = useState<TreatmentPlanAppointment[]>(
    [],
  );
  const [editingAppointmentId, setEditingAppointmentId] = useState<
    number | null
  >(null);
  const [appointmentDrafts, setAppointmentDrafts] = useState<
    Record<number, EditableAppointmentData>
  >({});
  const [appointmentInitialDrafts, setAppointmentInitialDrafts] = useState<
    Record<number, EditableAppointmentData>
  >({});
  const [savingAppointmentId, setSavingAppointmentId] = useState<number | null>(
    null,
  );
  const [deletingAppointmentId, setDeletingAppointmentId] = useState<
    number | null
  >(null);
  const [draggingAppointmentIndex, setDraggingAppointmentIndex] = useState<
    number | null
  >(null);
  const [dragOverAppointmentIndex, setDragOverAppointmentIndex] = useState<
    number | null
  >(null);
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const appointmentDraftsRef = useRef(appointmentDrafts);
  appointmentDraftsRef.current = appointmentDrafts;

  const syncAppointmentsFromPlan = useCallback((plan: TreatmentPlanResponse) => {
    setAppointments(
      [...plan.appointments].sort((a, b) => a.sortOrder - b.sortOrder),
    );
  }, []);

  const hasActiveEditSessions = editingAppointmentId !== null;
  const isDeletingAppointment = deletingAppointmentId !== null;
  const isAppointmentInteractionBlocked =
    hasActiveEditSessions || isDeletingAppointment;

  const sortedAppointments = [...appointments].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

  const hasOptions = appointments.some(
    (appt) => appt.conditionalPath && appt.conditionalPath.trim() !== "",
  );

  const updatePlanAppointments = useCallback(
    async (nextAppointments: TreatmentPlanAppointment[]) => {
      if (!treatmentPlan || !planId) return;

      const sortedNext = [...nextAppointments].map((appointment, index) => ({
        ...appointment,
        sortOrder: index + 1,
      }));

      const appointmentPayload = sortedNext.map((appointment, index) => {
        const payload: AppointmentUpdateData = {
          timeframeLabel:
            index < sortedNext.length - 1
              ? appointment.timeframeLabel || ""
              : "",
          conditionalPath: appointment.conditionalPath || undefined,
          description: appointment.description || undefined,
          sortOrder: index + 1,
          totalDurationMinutes: appointment.totalDurationMinutes,
          steps: [...appointment.steps]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((step, stepIndex) => {
              const stepPayload: StepUpdateData = {
                name: step.name,
                description: step.description || undefined,
                sortOrder: stepIndex + 1,
                durationMinutes: step.durationMinutes,
                fee: step.fee,
                isFeeEdited: step.isFeeEdited,
                teeth: step.teeth,
                surfaces: step.surfaces,
                codeSuggestions: step.codeSuggestions.map((suggestion) => ({
                  cdtCodeId: suggestion.cdtCodeId,
                })),
              };
              if (step.id > 0) {
                stepPayload.id = step.id;
              }
              return stepPayload;
            }),
        };
        if (appointment.id > 0) {
          payload.id = appointment.id;
        }
        return payload;
      });

      await saveTreatmentPlanUpdate(
        treatmentPlan.status,
        async (saveStatus, hasAppointmentUpdates) => {
          const updated = await treatmentPlanService.update(planId, {
            name: treatmentPlan.name,
            treatmentSummary: treatmentPlan.treatmentSummary,
            providerNote: treatmentPlan.providerNote,
            fee: treatmentPlan.fee || undefined,
            patientSummary: treatmentPlan.patientSummary || undefined,
            status: saveStatus,
            appointments: appointmentPayload,
            hasAppointmentUpdates,
          });

          setTreatmentPlan(updated);
          setAppointments(
            [...updated.appointments].sort((a, b) => a.sortOrder - b.sortOrder),
          );
        },
        { hasAppointmentUpdates: true },
      );
    },
    [planId, setTreatmentPlan, treatmentPlan],
  );

  const handleStartAppointmentEdit = useCallback(
    (appointment: TreatmentPlanAppointment) => {
      if (!canEditAppointments) return;
      if (
        editingAppointmentId !== null &&
        editingAppointmentId !== appointment.id
      ) {
        return;
      }

      const editable = convertToEditableAppointment(appointment);
      setEditingAppointmentId(appointment.id);
      setAppointmentDrafts((prev) => ({
        ...prev,
        [appointment.id]: editable,
      }));
      setAppointmentInitialDrafts((prev) => ({
        ...prev,
        [appointment.id]: cloneEditableAppointment(editable),
      }));
    },
    [canEditAppointments, editingAppointmentId],
  );

  const handleCancelAppointmentEdit = useCallback((appointmentId: number) => {
    const initialDraft = appointmentInitialDrafts[appointmentId];

    setAppointmentDrafts((prev) => {
      const next = { ...prev };
      delete next[appointmentId];
      return next;
    });
    setAppointmentInitialDrafts((prev) => {
      const next = { ...prev };
      delete next[appointmentId];
      return next;
    });
    setEditingAppointmentId(null);

    if (appointmentId < 0) {
      setAppointments((prev) =>
        prev.filter((appointment) => appointment.id !== appointmentId),
      );
      return;
    }

    if (initialDraft) {
      setAppointments((prev) =>
        prev.map((appointment) =>
          appointment.id === appointmentId
            ? restoreAppointmentFromInitialDraft(appointment, initialDraft)
            : appointment,
        ),
      );
    }
  }, [appointmentInitialDrafts]);

  const handleDraftAppointmentChange = useCallback(
    (
      appointmentId: number,
      field: keyof EditableAppointmentData,
      value: unknown,
    ) => {
      setAppointmentDrafts((prev) => {
        const draft = prev[appointmentId];
        if (!draft) return prev;

        const updated = { ...draft, [field]: value };
        if (field === "totalDurationMinutes") {
          updated.isDurationEdited = true;
        }
        if (field === "procedures") {
          updated.totalDurationMinutes = computeAppointmentDuration(
            value as EditableProcedureData[],
          );
          updated.isDurationEdited = false;
        }

        return {
          ...prev,
          [appointmentId]: updated,
        };
      });
    },
    [],
  );

  const handleDraftProcedureChange = useCallback(
    (
      appointmentId: number,
      procedureIndex: number,
      field: keyof EditableProcedureData,
      value: unknown,
    ) => {
      setAppointmentDrafts((prev) => {
        const draft = prev[appointmentId];
        if (!draft) return prev;
        const initialProcedure =
          appointmentInitialDrafts[appointmentId]?.procedures[procedureIndex];
        const procedures = draft.procedures.map((procedure, index) => {
          if (index !== procedureIndex) return procedure;
          const updated = { ...procedure, [field]: value };
          if (field === "fee") {
            let feeValue = String(updated.fee)
              .replace(/[^0-9.]/g, "")
              .replace(/(\..*)\./g, "$1");
            if (
              feeValue.length > 1 &&
              feeValue.startsWith("0") &&
              feeValue[1] !== "."
            ) {
              feeValue = feeValue.replace(/^0+/, "") || "0";
            }
            updated.fee = feeValue;
          }
          return syncProcedureIsFeeEdited(updated, initialProcedure);
        });
        return {
          ...prev,
          [appointmentId]: { ...draft, procedures },
        };
      });
    },
    [appointmentInitialDrafts],
  );

  const handleSaveAppointmentEdit = useCallback(
    async (appointmentId: number, durationMinutes?: number) => {
      if (!canEditAppointments) return;

      const baseDraft = appointmentDraftsRef.current[appointmentId];
      if (!baseDraft) return;

      const draft =
        durationMinutes !== undefined
          ? {
              ...baseDraft,
              totalDurationMinutes: durationMinutes,
              isDurationEdited: true,
            }
          : baseDraft;

      if (durationMinutes !== undefined) {
        setAppointmentDrafts((prev) => ({
          ...prev,
          [appointmentId]: draft,
        }));
      }

      setSavingAppointmentId(appointmentId);

      try {
        const updatedAppointment: TreatmentPlanAppointment = {
          id: appointmentId,
          timeframeLabel: draft.timeframeValue || "",
          conditionalPath: draft.conditionalPath || "",
          description: draft.description || "",
          notes: "",
          sortOrder: draft.sortOrder,
          totalDurationMinutes: draft.totalDurationMinutes ?? 0,
          totalFee: draft.procedures.reduce(
            (sum, procedure) => sum + (parseFloat(procedure.fee || "0") || 0),
            0,
          ),
          steps: draft.procedures.map((procedure, index) => ({
            id: procedure.id || index + 1,
            name: procedure.text || "",
            description: procedure.description || "",
            sortOrder: index + 1,
            durationMinutes: procedure.durationMinutes ?? 0,
            fee: parseFloat(procedure.fee || "0") || 0,
            isFeeEdited: procedure.isFeeEdited,
            teeth: procedure.teeth,
            surfaces: procedure.surfaces,
            codeSuggestions: procedure.resolvedCdtCodes
              .map((code, suggestionIndex) => {
                const match = cdtCodes.find((item) => item.code === code);
                if (!match) return null;
                return {
                  id: suggestionIndex + 1,
                  cdtCodeId: match.id,
                  code: match.code,
                  description: match.description,
                  defaultFee: match.defaultFee,
                };
              })
              .filter(
                (
                  suggestion,
                ): suggestion is {
                  id: number;
                  cdtCodeId: number;
                  code: string;
                  description: string;
                  defaultFee: number;
                } => suggestion !== null,
              ),
          })),
        };

        const nextAppointments = appointments.map((appointment) =>
          appointment.id === appointmentId ? updatedAppointment : appointment,
        );

        await updatePlanAppointments(nextAppointments);
        handleCancelAppointmentEdit(appointmentId);
        showNotification(t("treatmentPlan.appointmentSaved"));
      } catch (saveError) {
        console.error("Error saving appointment changes:", saveError);
        showNotification(t("editTreatmentPlan.failedToSave"), "error");
      } finally {
        setSavingAppointmentId(null);
      }
    },
    [
      appointments,
      canEditAppointments,
      cdtCodes,
      handleCancelAppointmentEdit,
      showNotification,
      t,
      updatePlanAppointments,
    ],
  );

  const handleDeleteAppointment = useCallback(
    async (appointmentId: number) => {
      if (!canEditAppointments) return;
      if (editingAppointmentId !== null) return;

      const remaining = appointments
        .filter((appointmentItem) => appointmentItem.id !== appointmentId)
        .map((appointmentItem, appointmentIndex) => ({
          ...appointmentItem,
          sortOrder: appointmentIndex + 1,
        }));

      setDeletingAppointmentId(appointmentId);

      try {
        await updatePlanAppointments(remaining);
        showNotification(t("treatmentPlan.appointmentDeleted"));
      } catch (deleteError) {
        console.error("Failed to delete appointment:", deleteError);
        showNotification(t("editTreatmentPlan.failedToSave"), "error");
        await onRefetchPlan(true);
      } finally {
        setDeletingAppointmentId(null);
      }
    },
    [
      appointments,
      canEditAppointments,
      editingAppointmentId,
      onRefetchPlan,
      showNotification,
      t,
      updatePlanAppointments,
    ],
  );

  const handleAddAppointment = useCallback(() => {
    if (!canEditAppointments) return;
    if (editingAppointmentId !== null) return;

    const newId = -Date.now();
    const newAppointment: TreatmentPlanAppointment = {
      id: newId,
      timeframeLabel: "",
      conditionalPath: "",
      description: "",
      notes: "",
      sortOrder: appointments.length + 1,
      totalDurationMinutes: 0,
      totalFee: 0,
      steps: [],
    };
    setAppointments((prev) => [...prev, newAppointment]);
    handleStartAppointmentEdit(newAppointment);
  }, [
    appointments.length,
    canEditAppointments,
    editingAppointmentId,
    handleStartAppointmentEdit,
  ]);

  const handleDraftCdtChange = useCallback(
    (
      appointmentId: number,
      procedureIndex: number,
      value: CDTAndCustomProcedureValue,
      allCdtCodes: string[],
      allDescriptions: string[],
      totalFee: number,
      totalDurationMinutes: number,
    ) => {
      setAppointmentDrafts((prev) => {
        const draft = prev[appointmentId];
        if (!draft) return prev;

        const initialProcedure =
          appointmentInitialDrafts[appointmentId]?.procedures[procedureIndex];

        const procedures = draft.procedures.map((procedure, index) => {
          if (index !== procedureIndex) return procedure;

          const nextProcedure = syncProcedureAfterCdtChange(
            {
              ...procedure,
              cdtCodes: value.cdtCodes,
              customProcedureId: value.customProcedureId,
              resolvedCdtCodes: allCdtCodes,
              explanation: allDescriptions.filter(Boolean).join("\n"),
              fee: String(totalFee),
              durationMinutes: totalDurationMinutes,
            },
            initialProcedure,
          );

          const hasCdtCodes = nextProcedure.resolvedCdtCodes.length > 0;
          if (hasCdtCodes) {
            return nextProcedure;
          }

          return {
            ...nextProcedure,
            teeth: [],
            surfaces: [],
          };
        });

        return {
          ...prev,
          [appointmentId]: {
            ...draft,
            procedures,
            totalDurationMinutes: computeAppointmentDuration(procedures),
            isDurationEdited: false,
          },
        };
      });
    },
    [appointmentInitialDrafts],
  );

  const handleAppointmentDragStart = useCallback(
    (event: DragEvent, index: number) => {
      if (!canEditAppointments) return;
      if (hasActiveEditSessions) return;
      if (isDeletingAppointment) return;

      dragItem.current = index;

      const dragHandle = event.currentTarget as HTMLElement;
      const appointmentWrapper = dragHandle.closest(
        "[data-appointment-wrapper]",
      ) as HTMLElement | null;
      if (appointmentWrapper) {
        const wrapperRect = appointmentWrapper.getBoundingClientRect();
        const offsetX = event.clientX - wrapperRect.left;
        const offsetY = event.clientY - wrapperRect.top;

        const dragImage = appointmentWrapper.cloneNode(true) as HTMLElement;
        dragImage.style.width = `${wrapperRect.width}px`;
        dragImage.style.position = "absolute";
        dragImage.style.top = "-9999px";
        dragImage.style.left = "0";
        dragImage.style.opacity = "1";
        dragImage.style.pointerEvents = "none";
        document.body.appendChild(dragImage);

        event.dataTransfer.setDragImage(dragImage, offsetX, offsetY);
        requestAnimationFrame(() => {
          document.body.removeChild(dragImage);
        });
      }

      setDraggingAppointmentIndex(index);
    },
    [canEditAppointments, hasActiveEditSessions, isDeletingAppointment],
  );

  const handleAppointmentDragOver = useCallback(
    (event: DragEvent, index: number) => {
      if (!canEditAppointments) return;
      if (hasActiveEditSessions) return;
      event.preventDefault();
      if (
        draggingAppointmentIndex !== null &&
        draggingAppointmentIndex !== index
      ) {
        dragOverItem.current = index;
        setDragOverAppointmentIndex(index);
      }
    },
    [canEditAppointments, draggingAppointmentIndex, hasActiveEditSessions],
  );

  const handleAppointmentDragLeave = useCallback((event: DragEvent) => {
    const relatedTarget = event.relatedTarget as Node | null;
    if (!event.currentTarget.contains(relatedTarget)) {
      setDragOverAppointmentIndex(null);
    }
  }, []);

  const handleAppointmentDragEnd = useCallback(() => {
    dragItem.current = null;
    dragOverItem.current = null;
    setDraggingAppointmentIndex(null);
    setDragOverAppointmentIndex(null);
  }, []);

  const handleAppointmentDrop = useCallback(
    async (event: DragEvent, dropIndex: number) => {
      if (!canEditAppointments) return;
      if (hasActiveEditSessions) return;
      event.preventDefault();

      const draggedIndex = dragItem.current;
      if (draggedIndex === null || draggedIndex === dropIndex) {
        dragItem.current = null;
        dragOverItem.current = null;
        setDraggingAppointmentIndex(null);
        setDragOverAppointmentIndex(null);
        return;
      }

      const reordered = [...appointments];
      const [dragged] = reordered.splice(draggedIndex, 1);
      reordered.splice(dropIndex, 0, dragged);
      setAppointments(
        reordered.map((appointment, index) => ({
          ...appointment,
          sortOrder: index + 1,
        })),
      );
      setDraggingAppointmentIndex(null);
      setDragOverAppointmentIndex(null);
      dragItem.current = null;
      dragOverItem.current = null;

      try {
        await updatePlanAppointments(reordered);
        showNotification(t("treatmentPlan.appointmentOrderUpdated"));
      } catch (reorderError) {
        console.error("Failed to persist appointment order:", reorderError);
        showNotification(t("editTreatmentPlan.failedToSave"), "error");
        await onRefetchPlan(true);
      }
    },
    [
      appointments,
      canEditAppointments,
      hasActiveEditSessions,
      onRefetchPlan,
      showNotification,
      t,
      updatePlanAppointments,
    ],
  );

  const getAppointmentDraft = useCallback(
    (appointment: TreatmentPlanAppointment) =>
      appointmentDrafts[appointment.id] ||
      convertToEditableAppointment(appointment),
    [appointmentDrafts],
  );

  const getAppointmentHasChanges = useCallback(
    (
      appointment: TreatmentPlanAppointment,
      isLastAppointment: boolean,
    ) => {
      const draft = getAppointmentDraft(appointment);
      const initialDraft = appointmentInitialDrafts[appointment.id];
      return initialDraft
        ? hasAppointmentDraftChanges(draft, initialDraft, isLastAppointment)
        : false;
    },
    [appointmentInitialDrafts, getAppointmentDraft],
  );

  return {
    appointments,
    syncAppointmentsFromPlan,
    sortedAppointments,
    hasOptions,
    editingAppointmentId,
    savingAppointmentId,
    deletingAppointmentId,
    draggingAppointmentIndex,
    dragOverAppointmentIndex,
    isAppointmentInteractionBlocked,
    handleStartAppointmentEdit,
    handleCancelAppointmentEdit,
    handleDraftAppointmentChange,
    handleDraftProcedureChange,
    handleSaveAppointmentEdit,
    handleDeleteAppointment,
    handleAddAppointment,
    handleDraftCdtChange,
    handleAppointmentDragStart,
    handleAppointmentDragOver,
    handleAppointmentDragLeave,
    handleAppointmentDragEnd,
    handleAppointmentDrop,
    getAppointmentDraft,
    getAppointmentHasChanges,
  };
}
